import { Menu, type MenuEntry } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * One plane the viewer can switch to.
 *
 * @property id - Stable plane id, reported to `onSwitch` when chosen.
 * @property label - Visible plane name.
 * @property requiredAtom - Permission atom the viewer must hold to open this
 *   plane. Omitted means every authenticated viewer may open it. A plane whose
 *   atom the viewer lacks is absent from the menu (plane-shell-substrate REQ-005).
 */
export interface PlaneTarget {
	id: string
	label: string
	requiredAtom?: string
}

/**
 * Props for {@link PlaneSwitcherMenu}.
 *
 * @property targets - All planes, in render order.
 * @property permissions - The viewer's permission atoms (ADR-0063).
 * @property current - The plane the viewer is on; shown disabled (marked
 *   current), never a switch action.
 * @property onSwitch - Reports the chosen plane's id. Never called for the
 *   current plane.
 * @property label - The trigger's text / accessible name.
 * @property className - Extra classes merged onto the trigger.
 */
export interface PlaneSwitcherMenuProps {
	targets: PlaneTarget[]
	permissions: readonly string[]
	current: string
	onSwitch?: (planeId: string) => void
	label?: string
	className?: string
}

function viewerMayOpen(target: PlaneTarget, permissions: readonly string[]): boolean {
	if (!target.requiredAtom) return true
	return permissions.includes(target.requiredAtom)
}

/**
 * The header plane-switch control: a menu of the planes the viewer may open.
 *
 * @remarks
 * - **Built on the `@today/ui-primitives` `Menu`** (React-Aria-backed, `ds-*`
 *   tokens), like {@link UserMenu} — not the shadcn dropdown.
 * - **Permission-driven:** a plane whose `requiredAtom` the viewer lacks is
 *   absent from the menu (ADR-0063; gating is on the atom, never a role).
 * - **Distinct from `TenantSwitcher`:** this switches *plane* (Workspace ↔
 *   Tenant Admin ↔ Platform), never the *tenant*. It invokes no tenant-changing
 *   callback.
 * - **The current plane** is shown disabled; activating it reports nothing (it
 *   is not a navigation) — enforced both by the disabled entry and an explicit
 *   guard.
 */
export function PlaneSwitcherMenu({
	targets,
	permissions,
	current,
	onSwitch,
	label = 'Switch plane',
	className,
}: PlaneSwitcherMenuProps): ReactElement {
	const entries: MenuEntry[] = targets
		.filter((target) => viewerMayOpen(target, permissions))
		.map((target) => ({ id: target.id, label: target.label, isDisabled: target.id === current }))

	function handleAction(planeId: string): void {
		if (planeId === current) return
		onSwitch?.(planeId)
	}

	return <Menu label={label} entries={entries} onAction={(key) => handleAction(String(key))} className={className} />
}
